import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { useDispatch } from "react-redux";

import {
  useGetCrmSyncStatusQuery,
  useRunCrmSyncMutation,
} from "../../store/apis/commercialDashboardApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { timeAgo } from "./dashboardFormat";

interface Props {
  /** Si puede lanzar una sincronización. ACCOUNT solo ve el estado. */
  canSync: boolean;
}

/**
 * La franja de arriba: cuándo se copió Twenty por última vez.
 *
 * Los datos del pipeline comercial salen de una copia que se refresca cada 15 minutos;
 * sin esta franja nadie sabría si lo que ve es de hace un minuto o de ayer.
 */
const CrmSyncBar: React.FC<Props> = ({ canSync }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data, isLoading } = useGetCrmSyncStatusQuery();
  const [runSync, { isLoading: syncing }] = useRunCrmSyncMutation();

  const status = data?.data;
  const lastRun = status?.lastRun;
  const lastRunFailed = Boolean(lastRun && lastRun.finishedAt && !lastRun.ok);

  const handleSync = async () => {
    try {
      await runSync().unwrap();
      dispatch(
        setToastMessage({
          message: "Copia de Twenty al día",
          severity: "success",
        })
      );
    } catch (error: unknown) {
      const { status: code, data: body } = (error ?? {}) as {
        status?: number;
        data?: { message?: string };
      };
      // 409: ya había una en marcha. La copia se pone al día igual, no es un fallo.
      if (code === 409) {
        dispatch(
          setToastMessage({
            message: "Ya había una sincronización en marcha",
            severity: "info",
          })
        );
        return;
      }
      dispatch(
        setToastMessage({
          message: body?.message ?? "No se pudo sincronizar con Twenty",
          severity: "error",
        })
      );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        px: 2,
        py: 1.25,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? "Comprobando la copia de Twenty…"
            : status?.lastSuccessAt
              ? `Sincronizado con Twenty ${timeAgo(status.lastSuccessAt)} · ${status.pursuits} empresas en el pipeline comercial · ${status.opportunities} oportunidades`
              : "Todavía no se ha sincronizado con Twenty"}
        </Typography>
        {canSync && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleSync}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
          >
            {syncing ? "Sincronizando…" : "Sincronizar ahora"}
          </Button>
        )}
      </Stack>
      {lastRunFailed && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          La última sincronización falló
          {lastRun?.error ? `: ${lastRun.error}` : ""}. Lo que ves es la copia
          anterior.
        </Alert>
      )}
    </Paper>
  );
};

export default CrmSyncBar;
